import { useState } from "react";
import PremiumHeader from "@/components/premium/PremiumHeader";
import SEO from "@/components/SEO";
import PremiumFooter from "@/components/premium/PremiumFooter";
import StickyContactBar from "@/components/premium/StickyContactBar";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const faqs = [
  { q: "What is the minimum stay requirement?", a: "We require a 90-day minimum stay. This property is designed for extended stays—corporate relocations, seasonal guests, travel nurses, and anyone seeking comfortable long-term housing." },
  { q: "What are the monthly rates?", a: "Two simple options: $4,250/month with utilities included for the first month (tenant covers utilities after month 1), or $4,500/month all-inclusive with every utility covered the entire stay. Minimum 90 days, up to 9 months. Contact us for exact dates." },
  { q: "What's included in the rental?", a: "The property is fully furnished with high-speed WiFi, full kitchen, washer/dryer, hot tub, game room, all linens, and basic essentials. Utilities packages are available." },
  { q: "How far is the beach?", a: "The Atlantic Ocean beach is a 3-minute walk from the property. Perfect for daily beach visits!" },
  { q: "Is parking available?", a: "Yes, the property has a large private driveway with space for 3+ vehicles." },
  { q: "Are pets allowed?", a: "Please contact us to discuss pet policies. We handle requests on a case-by-case basis." },
  { q: "What are check-in and check-out times?", a: "Standard check-in is 4:00 PM and check-out is 10:00 AM. For extended stays, we offer flexible move-in arrangements." },
  { q: "How do I book?", a: "Contact us directly through our form, email, or phone. We'll discuss your needs, confirm availability, and provide a lease agreement. No booking platform fees!" },
  { q: "What employers are nearby?", a: "We're minutes from L3Harris Technologies (15 min), Northrop Grumman (20 min), Health First hospitals (10 min), Florida Tech (12 min), and Melbourne's business district." },
  { q: "Is this suitable for working remotely?", a: "Absolutely. We have high-speed WiFi, dedicated workspace options, and a quiet neighborhood—perfect for remote work." },
];

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title="FAQs | Extended Stay Beach Rental Indialantic Florida | Paradise Beach Rental"
        description="Answers about our 90+ day furnished beach rental in Indialantic FL: monthly rates, pet policy, what's included, lease terms and how to book direct."
        path="/faq"
      />
      <PremiumHeader />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-[#0A1628]">
          <div className="container-premium text-center">
            <div className="gold-line mx-auto mb-6" />
            <span className="label-sm text-[#C9A962]">FAQ</span>
            <h1 className="headline-xl text-white mt-4 mb-6">Frequently Asked Questions</h1>
            <p className="body-lg text-white/70 max-w-2xl mx-auto">
              Everything you need to know about booking an extended stay at Beach Escape.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="section-lg">
          <div className="container-premium max-w-3xl">
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="bg-white border border-[#EDE7DB]"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="font-body font-semibold text-[#0A1628] pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-[#C9A962] flex-shrink-0 transition-transform ${openIndex === index ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6">
                          <p className="font-body text-[#0A1628]/70 leading-relaxed">{faq.a}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-[#0A1628]/60 mb-6">Still have questions?</p>
              <Link to="/contact">
                <Button className="bg-[#0A1628] text-white hover:bg-[#1E3A5F] rounded-none px-8 py-4">
                  Contact Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <PremiumFooter />
      <StickyContactBar />
    </div>
  );
};

export default FAQPage;
